import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { isGeneralManager } from "@/lib/roles";
import { Trophy, TrendingUp, TrendingDown, Minus, Search, Target, AlertCircle, Download, FileText, HelpCircle, ArrowDownUp, Activity, ClipboardList, Info, BarChart3 } from "lucide-react";

const GREEN = "#16A34A";
const RED = "#DC2626";
const AMBER = "#D97706";

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${color}1a`, color }}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-bold" style={{ color }}>{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </Card>
  );
}

function HealthBadge({ score }) {
  const c = score >= 70 ? GREEN : score >= 40 ? AMBER : RED;
  return (
    <span className="inline-flex items-center justify-center min-w-[2.75rem] px-2 py-0.5 rounded-full text-xs font-bold tabular-nums" style={{ background: `${c}1a`, color: c }} title={`Sağlık skoru: ${score}/100`}>
      {score}
    </span>
  );
}

function HealthTrendDialog({ committee, onClose }) {
  const [pts, setPts] = useState(null);
  useEffect(() => {
    if (!committee) { setPts(null); return; }
    setPts(null);
    api.get("/dashboard/health-trend", { params: { committee_no: committee.committee_no } })
      .then(({ data }) => setPts((data || []).map((d) => ({ date: (d.date || "").slice(5), health: d.health, secured: d.secured }))))
      .catch(() => setPts([]));
  }, [committee]);
  const last = pts && pts.length ? pts[pts.length - 1] : null;
  return (
    <Dialog open={!!committee} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="health-trend-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-[#0F2C59] dark:text-sky-300" />
            #{committee?.committee_no} {committee?.name} — Sağlık Trendi
          </DialogTitle>
        </DialogHeader>
        {last && (
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted-foreground">Güncel sağlık:</span>
            <HealthBadge score={last.health} />
            <span className="text-muted-foreground">Son 30 gün</span>
          </div>
        )}
        {pts === null ? (
          <Skeleton className="h-56 w-full" />
        ) : pts.length < 2 ? (
          <div className="h-56 grid place-items-center text-center text-sm text-muted-foreground" data-testid="health-trend-empty">
            Sağlık geçmişi henüz oluşmadı.<br />Skor her gün otomatik kaydedilir; birkaç gün içinde trend burada görünecek.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={pts} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} formatter={(v, n) => [n === "health" ? `${v}/100` : v, n === "health" ? "Sağlık" : "Kesin"]} />
              <ReferenceLine y={70} stroke={GREEN} strokeDasharray="4 4" />
              <ReferenceLine y={40} stroke={AMBER} strokeDasharray="4 4" />
              <Line type="monotone" dataKey="health" stroke="#0F2C59" strokeWidth={2.4} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

function pick(r, isHist, mode) {
  if (isHist) return r;
  return mode === "field" ? r.field : r.forecast;
}

function rowInsufficient(r, isHist, mode) {
  if (isHist) return false;
  const cov = r.faal ? r.contacted / r.faal : 0;
  if (mode === "field") return cov < 0.2;
  return !r.has_history && cov < 0.5;
}

// One year cell for the trend view
function TrendCell({ m }) {
  if (!m) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="leading-tight">
      <div className="font-semibold tabular-nums">{m.bize}</div>
      <div className="text-[11px] text-muted-foreground tabular-nums">%{m.share}</div>
    </div>
  );
}

// Tiny inline SVG sparkline of vote-share over 2018 -> 2022 -> 2026 (saha)
function Sparkline({ points, width = 84, height = 30 }) {
  const vals = points.filter((p) => p != null);
  if (vals.length < 2) return <span className="text-[11px] text-muted-foreground">yetersiz</span>;
  const min = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
  const n = points.length;
  const coords = points
    .map((p, i) => (p == null ? null : {
      x: (i / (n - 1)) * (width - 6) + 3,
      y: height - 4 - ((p - min) / range) * (height - 10),
    }))
    .filter(Boolean);
  const d = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
  const last = coords[coords.length - 1];
  const rising = vals[vals.length - 1] >= vals[0];
  const stroke = rising ? GREEN : RED;
  return (
    <svg width={width} height={height} className="overflow-visible" aria-hidden="true">
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={i === coords.length - 1 ? 2.6 : 1.6} fill={stroke} opacity={i === coords.length - 1 ? 1 : 0.45} />
      ))}
    </svg>
  );
}

export default function CommitteeScorecard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGM = isGeneralManager(user);
  const [params] = useSearchParams();
  const year = params.get("year");
  const isHist = year === "2018" || year === "2022";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [mode, setMode] = useState("field"); // field | forecast | trend
  const [trend, setTrend] = useState(null);
  const [sortKey, setSortKey] = useState("committee"); // committee | health | field | target
  const [sortDir, setSortDir] = useState("desc"); // desc = iyiden kötüye
  const [trendCommittee, setTrendCommittee] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/dashboard/committee-scorecard", { params: isHist ? { year } : {} })
      .then(({ data }) => setData(data))
      .catch(() => setData({ rows: [], total_committees: 0 }))
      .finally(() => setLoading(false));
  }, [year, isHist]);

  useEffect(() => {
    if (mode === "trend" && !trend) {
      api.get("/dashboard/committee-trend").then(({ data }) => setTrend(data)).catch(() => setTrend({ rows: [] }));
    }
  }, [mode, trend]);

  const rows = useMemo(() => {
    let r = (data?.rows || []).filter((x) => x.faal > 0);
    if (q.trim()) {
      const s = q.trim().toLocaleLowerCase("tr");
      r = r.filter((x) => `${x.committee_no} ${x.name}`.toLocaleLowerCase("tr").includes(s));
    }
    if (filter === "insufficient") return r.filter((x) => rowInsufficient(x, isHist, mode));
    if (filter === "winning") r = r.filter((x) => !rowInsufficient(x, isHist, mode) && pick(x, isHist, mode).winning);
    if (filter === "losing") r = r.filter((x) => !rowInsufficient(x, isHist, mode) && !pick(x, isHist, mode).winning);
    return r;
  }, [data, q, filter, isHist, mode]);

  const sorted = useMemo(() => {
    const r = [...rows];
    if (sortKey === "committee") return r.sort((a, b) => a.committee_no - b.committee_no);
    const val = (x) => {
      if (sortKey === "seats") return x.council_seats || 0;
      if (isHist) return x.turnout ? x.bize / x.turnout : 0;
      if (sortKey === "health") return x.health ?? 0;
      if (sortKey === "field") return x.faal ? x.contacted / x.faal : 0;
      if (sortKey === "belge_kesin") return x.kesin ? (x.belge || 0) / x.kesin : ((x.belge || 0) ? 1 : 0);
      if (sortKey === "belge_hedef") {
        const t = pick(x, isHist, mode).win_target;
        return t ? (x.belge || 0) / t : ((x.belge || 0) ? 1 : 0);
      }
      if (sortKey === "unmarked") return x.faal ? -((x.faal - x.contacted) / x.faal) : 0;
      const m = pick(x, isHist, mode);
      const denom = mode === "field" ? m.win_target : m.turnout;
      return denom ? m.bize / denom : 0;
    };
    return r.sort((a, b) => (sortDir === "asc" ? val(a) - val(b) : val(b) - val(a)));
  }, [rows, sortKey, sortDir, isHist, mode]);

  const trendRows = useMemo(() => {
    let r = trend?.rows || [];
    if (q.trim()) {
      const s = q.trim().toLocaleLowerCase("tr");
      r = r.filter((x) => `${x.committee_no} ${x.name}`.toLocaleLowerCase("tr").includes(s));
    }
    return r;
  }, [trend, q]);
  const trendT = useTableSort(trendRows);
  const scT = useTableSort(sorted);

  const doExport = async (kind) => {
    try {
      const sp = sortKey !== "committee" ? { sort: sortKey, dir: sortDir } : {};
      const order = scT.sorted.map((r) => r.committee_no).join(",");
      const p = isHist ? { year, order, only: 1, ...sp } : { mode, order, only: 1, ...sp };
      const ep = kind === "pdf" ? "/dashboard/committee-scorecard/export/pdf" : "/dashboard/committee-scorecard/export";
      const res = await api.get(ep, { params: p, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const base = isHist ? `secim_${year}_karne` : `komite_karnesi_${mode}`;
      a.download = `${base}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(kind === "pdf" ? "PDF indirildi" : "Excel indirildi");
    } catch {
      toast.error("Dışa aktarma başarısız");
    }
  };

  const doBriefing = async () => {
    try {
      const res = await api.get("/dashboard/committee-scorecard/briefing/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "baskan_brifingi.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Başkan brifingi indirildi");
    } catch {
      toast.error("Brifing oluşturulamadı");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="scorecard-loading">
        <Skeleton className="h-9 w-64" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const allRows = (data?.rows || []).filter((x) => x.faal > 0);
  const decided = allRows.filter((x) => !rowInsufficient(x, isHist, mode));
  const active = isHist ? (data?.active || 0) : allRows.length;
  const won = isHist ? (data?.won || 0) : decided.filter((x) => pick(x, isHist, mode).winning).length;
  const losing = isHist ? active - won : decided.length - won;
  const insufficientCount = isHist ? 0 : allRows.length - decided.length;
  const avgHealth = allRows.length ? Math.round(allRows.reduce((s, x) => s + (x.health || 0), 0) / allRows.length) : 0;
  const healthColor = avgHealth >= 70 ? GREEN : avgHealth >= 40 ? AMBER : RED;
  const totalSeats = allRows.reduce((s, x) => s + (x.council_seats || 0), 0);
  const wonSeats = allRows.filter((x) => !rowInsufficient(x, isHist, mode) && pick(x, isHist, mode).winning).reduce((s, x) => s + (x.council_seats || 0), 0);
  const isTrend = mode === "trend" && !isHist;

  return (
    <div className="space-y-6" data-testid="committee-scorecard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground">
            {isHist ? `${year} Seçim Sonuçları` : "Komite Karnesi"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isHist
              ? `Her komitenin ${year} seçiminde aldığı sonuç ve kazanma durumu`
              : isTrend
                ? "Her komite için 2018 → 2022 → 2026 (saha & tahmin) oy eğilimi"
                : mode === "field"
                  ? "FAAL firmaların güncel saha çalışması ile kazanma hedefi karşılaştırması"
                  : "Her komitenin 2026 tahmini ile kazanma hedefi karşılaştırması"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start">
          {!isHist && (
            <div className="inline-flex rounded-lg border border-border bg-muted p-0.5" data-testid="scorecard-mode-toggle">
              {[["field", "Saha Çalışması"], ["forecast", "2026 Tahmini"], ["trend", "Yıllar Arası"]].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setMode(k)}
                  data-testid={`scorecard-mode-${k}`}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${mode === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          )}
          {!isTrend && (
            <>
              <Button variant="outline" size="sm" onClick={() => navigate("/analizler/komite-analizleri")} data-testid="scorecard-analytics-btn" className="border-cyan-600 text-cyan-700 dark:border-cyan-400 dark:text-cyan-300">
                <BarChart3 className="h-4 w-4 mr-1.5" /> Komite Analizleri
              </Button>
              {isGM && (
                <>
                  <Button variant="outline" size="sm" onClick={() => doExport("xlsx")} data-testid="scorecard-export-btn">
                    <Download className="h-4 w-4 mr-1.5" /> Excel
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => doExport("pdf")} data-testid="scorecard-pdf-btn">
                    <FileText className="h-4 w-4 mr-1.5" /> PDF
                  </Button>
                  {!isHist && (
                    <Button variant="outline" size="sm" onClick={doBriefing} data-testid="scorecard-briefing-btn" className="border-[#0F2C59] text-[#0F2C59] dark:border-sky-400 dark:text-sky-300">
                      <ClipboardList className="h-4 w-4 mr-1.5" /> Başkan Brifingi
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {!isTrend && (
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${isHist ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-4`}>
          <StatCard icon={Trophy} label={isHist ? `Kazanılan komite (${year})` : mode === "field" ? "Hedefte komite (saha)" : "Kazanılan komite (tahmini)"} value={`${won} / ${active}`} color={GREEN} testid="score-won" />
          <StatCard icon={TrendingDown} label="Hedefin altında" value={losing} color={RED} testid="score-losing" />
          {isHist ? (
            <StatCard icon={Target} label="Veri girilen komite" value={active} color="#0F2C59" testid="score-active" />
          ) : (
            <>
              <StatCard icon={AlertCircle} label="Veri yetersiz" value={insufficientCount} color={AMBER} testid="score-insufficient" />
              <StatCard icon={Activity} label="Ortalama sağlık" value={avgHealth} color={healthColor} testid="score-avg-health" />
            </>
          )}
        </div>
      )}

      {isGM && mode === "field" && !isHist && rows.length > 0 && (() => {
        const oldT = rows.reduce((s, r) => s + Math.ceil((r.faal || 0) * (r.target_pct ?? 51) / 100), 0);
        const newT = rows.reduce((s, r) => s + (r.field?.win_target || 0), 0);
        return (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-amber-500/30 bg-amber-50 dark:bg-amber-950/20 px-4 py-2.5 text-sm" data-testid="target-compare-card">
            <span className="font-semibold text-amber-700 dark:text-amber-300">Hedef Karşılaştırma</span>
            <span className="text-muted-foreground">Eski hedef (FAAL %51): <b className="text-foreground" data-testid="target-compare-old">{oldT}</b></span>
            <span className="text-muted-foreground">Yeni hedef (2018 katılımı ×1,30 → yarısı+1): <b className="text-foreground" data-testid="target-compare-new">{newT}</b></span>
            <span className="font-semibold" style={{ color: newT <= oldT ? GREEN : RED }} data-testid="target-compare-diff">
              {newT <= oldT ? `${oldT - newT} oy daha kolay (−%${Math.round((oldT - newT) / (oldT || 1) * 100)})` : `${newT - oldT} oy daha zor`}
            </span>
          </div>
        );
      })()}

      {!isTrend && (
        <div className="flex items-center gap-2 rounded-lg border border-[#0F2C59]/20 bg-[#0F2C59]/5 dark:bg-sky-950/20 px-4 py-2.5 text-sm" data-testid="council-seats-banner">
          <span className="text-base">🏛️</span>
          <span className="text-muted-foreground">{isHist ? `${year} seçiminde kazanılan meclis üyeliği:` : "Şu an hedefte olan komitelerden kazanılabilecek meclis üyeliği:"}</span>
          <b className="text-[#0F2C59] dark:text-sky-300 text-base" data-testid="won-seats">{wonSeats}</b>
          <span className="text-muted-foreground">/ {totalSeats} toplam üyelik</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Komite ara..." className="pl-9" data-testid="scorecard-search" />
        </div>
        {!isTrend && (
          <div className="flex flex-wrap gap-2 items-center">
            {[["all", "Tümü"], ["winning", "Hedefte"], ["losing", "Altında"], ...(isHist ? [] : [["insufficient", "Veri yetersiz"]])].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`scorecard-filter-${k}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === k ? "bg-[#0F2C59] text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
              >
                {l}
              </button>
            ))}
            <div className="w-px h-6 bg-border mx-1 hidden sm:block" />
            <Select value={sortKey} onValueChange={setSortKey}>
              <SelectTrigger className="h-9 w-[190px]" data-testid="scorecard-sort-key"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="committee">Sırala: Komite No</SelectItem>
                <SelectItem value="health">Sırala: Sağlık</SelectItem>
                <SelectItem value="seats">Sırala: Meclis Üyesi</SelectItem>
                <SelectItem value="field">Sırala: Saha İlerlemesi</SelectItem>
                <SelectItem value="target">Sırala: Hedef İlerlemesi</SelectItem>
                <SelectItem value="belge_kesin">Sırala: Belge / Kesin Oy %</SelectItem>
                <SelectItem value="belge_hedef">Sırala: Belge / Hedef Oy %</SelectItem>
                <SelectItem value="unmarked">Sırala: İşaretsiz Oranı (az iyi)</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline" size="sm"
              disabled={sortKey === "committee"}
              onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
              data-testid="scorecard-sort-dir"
              title={sortDir === "desc" ? "İyiden kötüye" : "Kötüden iyiye"}
            >
              <ArrowDownUp className="h-4 w-4 mr-1.5" />{sortDir === "desc" ? "İyiden Kötüye" : "Kötüden İyiye"}
            </Button>
          </div>
        )}
      </div>

      {isTrend ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trend-table">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <trendT.Th sortKey="tr-komite" get={(r) => r.name} text className="px-4 py-3 font-medium">Komite</trendT.Th>
                  <trendT.Th sortKey="tr-2018" get={(r) => r.y2018?.share} className="px-4 py-3 font-medium text-center">2018</trendT.Th>
                  <trendT.Th sortKey="tr-2022" get={(r) => r.y2022?.share} className="px-4 py-3 font-medium text-center">2022</trendT.Th>
                  <trendT.Th sortKey="tr-saha" get={(r) => r.field?.share} className="px-4 py-3 font-medium text-center">2026 Saha</trendT.Th>
                  <trendT.Th sortKey="tr-tahmin" get={(r) => r.forecast?.share} className="px-4 py-3 font-medium text-center">2026 Tahmin</trendT.Th>
                  <trendT.Th sortKey="tr-egilim" get={(r) => { const b = r.y2022 || r.y2018; return b ? r.field.share - b.share : null; }} className="px-4 py-3 font-medium text-center w-40">Eğilim</trendT.Th>
                </tr>
              </thead>
              <tbody>
                {trendT.sorted.map((r) => {
                  const base = r.y2022 || r.y2018;
                  const diff = base ? r.field.share - base.share : null;
                  const spark = [r.y2018?.share ?? null, r.y2022?.share ?? null, r.field.share];
                  return (
                    <tr key={r.committee_no} className="border-b border-border/60 hover:bg-muted/40 transition-colors" data-testid={`trend-row-${r.committee_no}`}>
                      <td className="px-4 py-3">
                        <span className="text-muted-foreground">#{r.committee_no}</span> <span className="font-medium">{r.name}</span>
                      </td>
                      <td className="px-4 py-3 text-center"><TrendCell m={r.y2018} /></td>
                      <td className="px-4 py-3 text-center"><TrendCell m={r.y2022} /></td>
                      <td className="px-4 py-3 text-center" style={{ color: GREEN }}><TrendCell m={r.field} /></td>
                      <td className="px-4 py-3 text-center" style={{ color: "#0F2C59" }}><TrendCell m={r.forecast} /></td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-center gap-1">
                          <Sparkline points={spark} />
                          {diff === null ? (
                            <span className="text-[11px] text-muted-foreground">geçmiş yok</span>
                          ) : diff > 1 ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: GREEN }}><TrendingUp className="h-3.5 w-3.5" /> +{diff}p</span>
                          ) : diff < -1 ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: RED }}><TrendingDown className="h-3.5 w-3.5" /> {diff}p</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground"><Minus className="h-3.5 w-3.5" /> sabit</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {trendRows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">{trend ? "Gösterilecek komite yok." : "Yükleniyor..."}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="scorecard-table">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <scT.Th sortKey="sc-komite" get={(r) => r.name} text className="px-4 py-3 font-medium">Komite</scT.Th>
                  <scT.Th sortKey="sc-uye" get={(r) => r.council_seats ?? 0} className="px-4 py-3 font-medium text-center w-16" title="Meclis üyesi sayısı">Üye</scT.Th>
                  <scT.Th sortKey="sc-faal" get={(r) => (isHist ? pick(r, isHist, mode).turnout : r.faal)} className="px-4 py-3 font-medium text-right">{isHist ? "Seçmen" : "FAAL"}</scT.Th>
                  {!isHist && <scT.Th sortKey="sc-aranan" get={(r) => (r.faal ? r.contacted / r.faal : 0)} className="px-4 py-3 font-medium text-center w-44">Saha İlerleme (aranan)</scT.Th>}
                  <scT.Th sortKey="sc-bize" get={(r) => pick(r, isHist, mode).bize} className="px-4 py-3 font-medium text-right">Bize</scT.Th>
                  <scT.Th sortKey="sc-karsi" get={(r) => pick(r, isHist, mode).karsi} lowBest className="px-4 py-3 font-medium text-right">Karşı</scT.Th>
                  <scT.Th sortKey="sc-hedef" get={(r) => pick(r, isHist, mode).win_target} className="px-4 py-3 font-medium text-right">Hedef</scT.Th>
                  <scT.Th sortKey="sc-pay" get={(r) => { const m = pick(r, isHist, mode); const d = mode === "field" && !isHist ? m.win_target : m.turnout; return d ? m.bize / d : 0; }} className="px-4 py-3 font-medium text-center w-56">{isHist || mode === "forecast" ? "Oy Payı (kullananlarda)" : "Hedef ilerlemesi"}</scT.Th>
                  {!isHist && <scT.Th sortKey="sc-saglik" get={(r) => r.health ?? 0} className="px-4 py-3 font-medium text-center w-24">Sağlık</scT.Th>}
                  {!isHist && <scT.Th sortKey="sc-belge" get={(r) => r.belge_pct ?? 0} className="px-4 py-3 font-medium text-center w-40" title="KESİN oyun %60'ı kadar yetki belgesi hedefi">Yetki Belgesi</scT.Th>}
                  {!isHist && <scT.Th sortKey="sc-belge-kesin" get={(r) => (r.kesin ? (r.belge || 0) / r.kesin : 0)} className="px-4 py-3 font-medium text-center w-28" title="Alınan yetki belgelerinin KESİN oylara oranı">Belge/Kesin %</scT.Th>}
                  {!isHist && <scT.Th sortKey="sc-belge-hedef" get={(r) => { const t = pick(r, isHist, mode).win_target; return t ? (r.belge || 0) / t : 0; }} className="px-4 py-3 font-medium text-center w-28" title="Alınan yetki belgelerinin kazanma hedefine oranı">Belge/Hedef %</scT.Th>}
                  {!isHist && <scT.Th sortKey="sc-sahis90" get={(r) => r.sahis_90_pct ?? 0} className="px-4 py-3 font-medium text-center w-28" title="Şahıs şirketlerinde '%90 Gelecek' işaretlenme oranı (şahıs firmalar yetki belgesi almaz)">%90 İşaretli</scT.Th>}
                  {!isHist && <scT.Th sortKey="sc-isaretsiz" get={(r) => (r.faal ? Math.max(r.faal - r.contacted, 0) / r.faal : 0)} lowBest className="px-4 py-3 font-medium text-center w-32" title="2026 tahmini girilmemiş (işaretsiz) firmaların FAAL seçmene oranı">İşaretsiz %</scT.Th>}
                  <scT.Th sortKey="sc-durum" get={(r) => pick(r, isHist, mode).margin} className="px-4 py-3 font-medium text-right">Durum</scT.Th>
                </tr>
              </thead>
              <tbody>
                {scT.sorted.map((r, ri) => {
                  const m = pick(r, isHist, mode);
                  const insuf = rowInsufficient(r, isHist, mode);
                  const denom = mode === "field" && !isHist ? m.win_target : m.turnout;
                  const share = denom ? Math.round((m.bize / denom) * 100) : 0;
                  const cov = r.faal ? Math.round((r.contacted / r.faal) * 100) : 0;
                  return (
                    <tr key={r.committee_no} onClick={() => !isHist && setTrendCommittee(r)} className={`border-b border-border/60 hover:bg-muted/40 transition-colors ${!isHist ? "cursor-pointer" : ""}`} data-testid={`scorecard-row-${r.committee_no}`} title={!isHist ? "Sağlık trendini gör" : undefined}>
                      <td className="px-4 py-3">
                        {sortKey !== "committee" && <span className="inline-flex items-center justify-center h-5 min-w-[1.35rem] px-1 mr-1.5 rounded bg-muted text-[11px] font-bold text-muted-foreground align-middle" data-testid={`scorecard-rank-${r.committee_no}`}>{ri + 1}</span>}
                        <span className="text-muted-foreground">#{r.committee_no}</span> <span className="font-medium">{r.name}</span>
                        {!isHist && (r.belge_target ?? 0) > 0 && (r.belge_pct ?? 0) < 30 && (
                          <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white animate-pulse" data-testid={`belge-acil-${r.committee_no}`} title={`Yetki belgesi hedefin çok gerisinde: ${r.belge ?? 0}/${r.belge_target ?? 0} (%${r.belge_pct ?? 0})`}>ACİL</span>
                        )}
                        {mode === "forecast" && !isHist && r.no_rival_only && (
                          <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300" data-testid={`no-rival-badge-${r.committee_no}`} title="Geçmiş seçim(ler)de rakip yoktu (karşıya 0 oy). Katılım temsili olmadığı için bu seçimler 2026 tahminine esas alınmadı.">
                            tahmine esas rakipli seçim yok
                          </span>
                        )}
                        {mode === "forecast" && !isHist && !r.has_history && !r.no_rival_only && <span className="ml-2 text-[10px] text-amber-600" title="Geçmiş seçim verisi yok">geçmiş yok</span>}
                      </td>
                      <td className="px-4 py-3 text-center tabular-nums font-bold text-[#0F2C59] dark:text-sky-300" data-testid={`scorecard-seats-${r.committee_no}`} title="Meclis üyesi sayısı">{r.council_seats ?? 0}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{r.faal}</td>
                      {!isHist && (
                        <td className="px-4 py-3" data-testid={`scorecard-coverage-${r.committee_no}`}>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                              <div className="h-full bg-[#0F2C59] dark:bg-sky-400" style={{ width: `${Math.min(cov, 100)}%` }} />
                            </div>
                            <span className="tabular-nums text-xs text-muted-foreground w-16 text-right">{r.contacted}/{r.faal}</span>
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3 text-right tabular-nums font-semibold" style={{ color: insuf ? "inherit" : GREEN }}>{m.bize}</td>
                      <td className="px-4 py-3 text-right tabular-nums" style={{ color: insuf ? "inherit" : RED }}>{m.karsi}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#0F2C59] dark:text-sky-300 font-medium">
                        <span className="inline-flex items-center gap-1 justify-end">
                          {m.win_target}
                          {mode === "field" && !isHist && r.field_target_pct != null && (
                            <span data-testid={`field-target-info-${r.committee_no}`} className="cursor-help" title={`Saha hedefi: 2018 katılımı %${(r.field_target_pct / 1.3).toFixed(1)} × 1,30 = beklenen katılım %${r.field_target_pct}; bu seçmen sayısının yarısından 1 fazlası (yukarı yuvarlanır)`}>
                              <Info className="h-3.5 w-3.5 text-amber-500" />
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`flex items-center gap-2 ${insuf ? "opacity-40" : ""}`}>
                          <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden relative">
                            <div className="h-full" style={{ width: `${Math.min(share, 100)}%`, background: m.winning ? GREEN : RED }} />
                            {(isHist || mode === "forecast") && (
                              <div className="absolute top-0 bottom-0 w-px bg-[#0F2C59] dark:bg-sky-300" style={{ left: `${r.target_pct}%` }} title={`Hedef %${r.target_pct}`} />
                            )}
                          </div>
                          <span className="tabular-nums text-xs w-9 text-right font-semibold">%{share}</span>
                        </div>
                      </td>
                      {!isHist && (
                        <td className="px-4 py-3 text-center" data-testid={`scorecard-health-${r.committee_no}`}>
                          <div className="inline-flex items-center gap-1">
                            <HealthBadge score={r.health ?? 0} />
                            {typeof r.health_delta === "number" && r.health_delta !== 0 && (
                              <span className={`inline-flex items-center text-[10px] font-bold ${r.health_delta > 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`scorecard-health-delta-${r.committee_no}`} title={`Son 7 günde ${r.health_delta > 0 ? "+" : ""}${r.health_delta} puan`}>
                                {r.health_delta > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(r.health_delta)}
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      {!isHist && (
                        <td className="px-4 py-3" data-testid={`scorecard-belge-${r.committee_no}`}>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                              <div className="h-full" style={{ width: `${Math.min(r.belge_pct ?? 0, 100)}%`, background: (r.belge_pct ?? 0) >= 100 ? GREEN : (r.belge_pct ?? 0) >= 60 ? AMBER : RED }} />
                            </div>
                            <span className="tabular-nums text-xs text-muted-foreground w-14 text-right" title={`Hedef: KESİN ${r.kesin ?? 0} × %60 = ${r.belge_target ?? 0}`}>{r.belge ?? 0}/{r.belge_target ?? 0}</span>
                          </div>
                        </td>
                      )}
                      {!isHist && (
                        <td className="px-4 py-3 text-center tabular-nums font-semibold" data-testid={`scorecard-belge-kesin-${r.committee_no}`}
                          title={`Alınan belge ${r.belge ?? 0} / KESİN oy ${r.kesin ?? 0}`}
                          style={{ color: (r.kesin ? (r.belge || 0) / r.kesin : 0) >= 0.6 ? GREEN : (r.kesin ? (r.belge || 0) / r.kesin : 0) >= 0.3 ? AMBER : RED }}>
                          {r.kesin ? `%${Math.round((r.belge || 0) / r.kesin * 100)}` : "—"}
                        </td>
                      )}
                      {!isHist && (
                        <td className="px-4 py-3 text-center tabular-nums font-semibold" data-testid={`scorecard-belge-hedef-${r.committee_no}`}
                          title={`Alınan belge ${r.belge ?? 0} / hedef oy ${m.win_target}`}>
                          {m.win_target ? `%${Math.round((r.belge || 0) / m.win_target * 100)}` : "—"}
                        </td>
                      )}
                      {!isHist && (
                        <td className="px-4 py-3 text-center tabular-nums font-semibold" data-testid={`scorecard-sahis90-${r.committee_no}`}
                          title={`Şahıs şirketi ${r.sahis ?? 0} · "%90 Gelecek" işaretli ${r.sahis_90 ?? 0}`}
                          style={{ color: (r.sahis ?? 0) === 0 ? "inherit" : (r.sahis_90_pct ?? 0) >= 60 ? GREEN : (r.sahis_90_pct ?? 0) >= 30 ? AMBER : RED }}>
                          {(r.sahis ?? 0) ? `%${r.sahis_90_pct ?? 0}` : "—"}
                          {(r.sahis ?? 0) ? <span className="block text-[11px] font-normal text-muted-foreground tabular-nums">{r.sahis_90 ?? 0}/{r.sahis}</span> : null}
                        </td>
                      )}
                      {!isHist && (
                        <td className="px-4 py-3 text-center tabular-nums font-semibold" data-testid={`scorecard-unmarked-${r.committee_no}`}
                          title={`İşaretsiz ${Math.max(r.faal - r.contacted, 0)} / FAAL ${r.faal}`}
                          style={{ color: (r.faal ? (r.faal - r.contacted) / r.faal : 0) <= 0.2 ? GREEN : (r.faal ? (r.faal - r.contacted) / r.faal : 0) <= 0.5 ? AMBER : RED }}>
                          {r.faal ? `%${Math.round(Math.max(r.faal - r.contacted, 0) / r.faal * 100)}` : "—"}
                          {r.faal ? <span className="block text-[11px] font-normal text-muted-foreground tabular-nums">{Math.max(r.faal - r.contacted, 0)} firma</span> : null}
                        </td>
                      )}
                      <td className="px-4 py-3 text-right">
                        {insuf ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${AMBER}1a`, color: AMBER }} title="Yeterli saha/geçmiş verisi yok — kazanma tahmini yapılmıyor">
                            <HelpCircle className="h-3.5 w-3.5" /> Veri yetersiz
                          </span>
                        ) : m.winning ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${GREEN}1a`, color: GREEN }}>
                            <TrendingUp className="h-3.5 w-3.5" /> +{m.margin}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${RED}1a`, color: RED }}>
                            <TrendingDown className="h-3.5 w-3.5" /> {m.margin}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={isHist ? 8 : 14} className="px-4 py-10 text-center text-muted-foreground">Gösterilecek komite yok.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        {isHist
          ? `Hedef = oy kullananların (Bize+Karşı) %${rows[0]?.target_pct ?? 51}'i. ${year} sonuçları girilen değerlerden gösterilir.`
          : isTrend
            ? "Her hücrede Bize oyu ve oy kullananlar içindeki payı (%) gösterilir. Eğilim, 2022 (yoksa 2018) payı ile 2026 saha payı arasındaki puan farkıdır."
            : mode === "field"
              ? `Saha çalışması: FAAL firmalar arasında KESİN + yetki belgesi güvence altındaki oylar baz alınır. Hedef = komitenin 2018 katılım oranının %30 fazlası kadar firma oy kullanır varsayımıyla, bu sayının yarısından 1 fazlası (küsurat yukarı yuvarlanır). 2018 verisi olmayan komitede tüm komitelerin ortalama 2018 katılımı kullanılır. Firmaların %20'sinden azı işaretlenmişse "veri yetersiz" sayılır.`
              : `Hedef = oy kullananların (Bize+Karşı) %${rows[0]?.target_pct ?? 51}'i. Tahmin, saha işaretleri + komitenin kendi geçmiş ortalaması ile hesaplanır. Geçmişi olmayan ve az işaretli komiteler "veri yetersiz" sayılır.`}
      </p>
      <HealthTrendDialog committee={trendCommittee} onClose={() => setTrendCommittee(null)} />
    </div>
  );
}
